import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;
export const DialogPortal = RadixDialog.Portal;

export const DialogOverlay = forwardRef<
  ElementRef<typeof RadixDialog.Overlay>,
  ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(function DialogOverlay({ className, ...rest }, ref) {
  return (
    <RadixDialog.Overlay
      ref={ref}
      className={cn(
        // Translucent scrim only — no backdrop-blur (paper, not glass).
        'fixed inset-0 z-overlay bg-scrim',
        // Duration must ride the same data-state variants as animate-in/out —
        // the variant's attribute selector out-specifies a bare duration class.
        'ease-out-strong data-[state=open]:duration-modal data-[state=closed]:duration-modal',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'motion-reduce:animate-none',
        className,
      )}
      {...rest}
    />
  );
});

interface DialogContentProps extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  /** Whether to render a top-right close button. Defaults to true. */
  showClose?: boolean;
}

export const DialogContent = forwardRef<ElementRef<typeof RadixDialog.Content>, DialogContentProps>(
  function DialogContent({ className, showClose = true, children, ...rest }, ref) {
    return (
      <RadixDialog.Portal>
        <DialogOverlay />
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'fixed left-1/2 top-1/2 z-overlay w-[min(92vw,480px)] -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl border border-line-2 bg-card p-5 shadow-window outline-none',
            // Duration must ride the same data-state variants as animate-in/out —
            // the variant's attribute selector out-specifies a bare duration class.
            'ease-out-strong data-[state=open]:duration-modal data-[state=closed]:duration-modal',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'motion-reduce:animate-none',
            className,
          )}
          {...rest}
        >
          {children}
          {showClose ? (
            <RadixDialog.Close
              className={cn(
                'absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-t3',
                'pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
                'transition-colors can-hover:hover:bg-rail can-hover:hover:text-t1',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              )}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </RadixDialog.Close>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);

export const DialogTitle = forwardRef<
  ElementRef<typeof RadixDialog.Title>,
  ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(function DialogTitle({ className, ...rest }, ref) {
  return (
    <RadixDialog.Title
      ref={ref}
      // break-words: titles often interpolate user content ("Delete {name}?") —
      // an unbroken string must wrap, not overflow the dialog.
      // pe-8/pe-12: the close × is absolute (right-3, 28px / 44px coarse) and
      // intrudes 20px/36px past the p-5 content box — long titles must not run
      // under it.
      className={cn(
        'break-words pe-8 pointer-coarse:pe-12 text-title font-medium text-t1',
        className,
      )}
      {...rest}
    />
  );
});

export const DialogDescription = forwardRef<
  ElementRef<typeof RadixDialog.Description>,
  ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(function DialogDescription({ className, ...rest }, ref) {
  return (
    <RadixDialog.Description
      ref={ref}
      className={cn('mt-1 text-body text-t2', className)}
      {...rest}
    />
  );
});

export function DialogFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-5 flex items-center justify-end gap-2', className)} {...rest} />;
}
